import { useNavigate } from 'react-router-dom';

import { AcademicEnrollmentType } from '@backrs_ui/common-ui';

import { areAllLifeReadinessQuestionsAnswered } from '@backrs_ui/grow-ui/src/pages/LifeReadiness/utils/lifeReadinessQuestionnaire.util';
import { filterEditProfileProfileOptions } from '@backrs_ui/grow-ui/src/pages/Profile/utils/options.helpers';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetQuestionsQuery,
  useGetQuestionUserLevelsQuery,
} from '@backrs_ui/grow-ui/src/redux/lifeReadiness/lifeReadiness.api';
import {
  useFetchProfileValueOptionsQuery,
  UserProfileResponse,
  ProfileValueOptionResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import {
  assessmentChecklistConfig,
  ChecklistItem,
  ChecklistProfileData,
} from './assessmentChecklist.config';
import { ChecklistDataOverrides } from '../assessMyTrack.types';

const getChecklistItems = (
  allItems: ChecklistItem[],
  enrollmentType: AcademicEnrollmentType | undefined,
): ChecklistItem[] => {
  if (!enrollmentType) return [];

  return allItems.filter((item) => !item.enrollmentType || item.enrollmentType === enrollmentType);
};

export const useAssessmentChecklist = (
  profileData: UserProfileResponse | undefined,
  checklistDataOverrides?: ChecklistDataOverrides,
): {
  requiredItems: ChecklistItem[];
  optionalItems: ChecklistItem[];
  progress: number;
  isChecklistComplete: boolean;
  profileOptions: ProfileValueOptionResponse | undefined;
  isBlueprintComplete: boolean;
} => {
  const navigate = useNavigate();

  const userId = useAppSelector((state) => state.user.user?.id);
  const doesUserHaveBlueprint = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.user_responses,
  );

  const { data: lifeReadinessQuestions = [] } = useGetQuestionsQuery(undefined);
  const { data: lifeReadinessUserLevels = [] } = useGetQuestionUserLevelsQuery(userId);
  const { data: unfilteredProfileOptions } = useFetchProfileValueOptionsQuery(undefined);
  const profileOptions = filterEditProfileProfileOptions(unfilteredProfileOptions);

  const isLifeReadinessComplete = areAllLifeReadinessQuestionsAnswered(
    lifeReadinessQuestions,
    lifeReadinessUserLevels,
  );

  const isBlueprintComplete = checklistDataOverrides?.career_user_response_set_id
    ? !!checklistDataOverrides.career_user_response_set_id
    : !!doesUserHaveBlueprint;

  // merge override data with actual profile data if provided
  const mergedProfile: ChecklistProfileData['profile'] | undefined =
    profileData?.profile || checklistDataOverrides
      ? {
          ...profileData?.profile,
          ...checklistDataOverrides,
        }
      : undefined;

  const profileDataToUse: ChecklistProfileData | undefined = mergedProfile
    ? { profile: mergedProfile }
    : undefined;

  const allChecklistItems: ChecklistItem[] = assessmentChecklistConfig.map((config) => ({
    label: config.label,
    isComplete: profileDataToUse
      ? config.getIsComplete({
          profileData: profileDataToUse,
          isBlueprintComplete,
          isLifeReadinessComplete,
        })
      : false,
    description: profileDataToUse
      ? config.getDescription({
          profileData: profileDataToUse,
          profileOptions,
          doesUserHaveBlueprint: isBlueprintComplete,
          isLifeReadinessComplete,
        })
      : '',
    fieldKey: config.fieldKey,
    enrollmentType: config.enrollmentType,
    optional: config.optional,
    customOnEdit: config.customOnEdit
      ? () => {
          if (config.customOnEdit) {
            config.customOnEdit(navigate, !!isBlueprintComplete);
          }
        }
      : undefined,
  }));

  const enrollmentType = profileDataToUse?.profile?.academicEnrollmentTypes?.level;
  const checklistItems = getChecklistItems(allChecklistItems, enrollmentType);

  const requiredItems = checklistItems.filter((item) => !item.optional);
  const optionalItems = checklistItems.filter((item) => item.optional);
  const completedRequiredItems = requiredItems.filter((item) => item.isComplete).length;
  const progress =
    requiredItems.length > 0 ? (completedRequiredItems / requiredItems.length) * 100 : 0;

  const isChecklistComplete = progress === 100;

  return {
    requiredItems,
    optionalItems,
    progress,
    isChecklistComplete,
    profileOptions,
    isBlueprintComplete,
  };
};
